// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import type { UpdateWorkOrderItemBody, WorkOrder, WorkOrderItem } from '@evolvemep/api-ts';

import { GridMenu } from 'components/Mantine/GridMenu';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useManufacturers } from 'hooks/typeCacheHooks/useManufacturers';
import { useUnitOfMeasure } from 'hooks/typeCacheHooks/useUnitOfMeasure';
import { useIsMobile } from 'hooks/useIsMobile';
import { useHasAccess } from 'hooks/user/useHasAccess';
import { SPOOL_UOM_ID } from 'modules/Shop/WorkOrders/WorkOrderDetail/constants';
import { AddToCatalogModal } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/AddToCatalogModal';
import { AddToShipmentFromWorkOrderModal } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/AddToShipmentFromWorkOrderModal';
import { EditItemPopover } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/EditItemPopover';

type Props = {
  item: WorkOrderItem;
  workOrder?: WorkOrder;
  editable: boolean;
  refreshItem: () => void;
  /** Some operations (e.g. adding to shipment can affect other items. This callback refreshes the item list.)
   *  If not provided, any operation that affects other items should not be allowed.
   **/
  onMultipleItemsUpdated?: () => void;
  updateItem: (body: UpdateWorkOrderItemBody) => Promise<void>;
  onDeleteItem?: (item: WorkOrderItem) => void;
  onViewDetails?: () => void;
  loadingOverride?: boolean;
};

export const EditItemMenu = ({
  item,
  workOrder,
  editable,
  refreshItem,
  onMultipleItemsUpdated,
  updateItem,
  onDeleteItem,
  onViewDetails,
  loadingOverride,
}: Props) => {
  const isMobile = useIsMobile();
  const hasModifyCatalogAccess = useHasAccess('catalog', 'write');
  const { uoms } = useUnitOfMeasure();
  const { manufacturers } = useManufacturers();

  const [saving, setSaving] = useState(false);
  const onSubmit = async (...params: Parameters<typeof updateItem>) => {
    setSaving(true);
    void updateItem(...params).finally(() => setSaving(false));
  };

  const canAddToCatalog =
    hasModifyCatalogAccess &&
    item.unitOfMeasureId !== SPOOL_UOM_ID &&
    'workOrderItemId' in item &&
    isNil(item.parentWorkOrderItemId) &&
    // Catalog is not yet supported on Mobile
    !isMobile;

  const canAddToShipment =
    isNotNil(workOrder) &&
    'workOrderItemId' in item &&
    isNil(item.parentWorkOrderItemId) &&
    isNotNil(onMultipleItemsUpdated) &&
    item.totalAllocatedQuantity < item.totalQuantity &&
    // Shipments are not yet supported on Mobile
    !isMobile;

  return (
    <GridMenu
      options={[
        {
          value: 'View item',
          hidden: isNil(onViewDetails),
        },
        {
          value: 'Edit properties',
          hidden: !editable,
        },
        {
          value: 'Add item to catalog',
          hidden: !canAddToCatalog,
        },
        {
          value: 'Add to shipping order',
          hidden: !canAddToShipment,
        },
        {
          value: 'Delete',
          hidden: isNil(onDeleteItem) || !editable,
        },
      ]}
      onMenuItemSelected={(menuItemSelected) => {
        if (menuItemSelected === 'View item') {
          onViewDetails?.();
          return true;
        }
        if (menuItemSelected === 'Delete') {
          onDeleteItem?.(item);
          return true;
        }
        return false;
      }}
      loading={saving || loadingOverride}
    >
      {(menuItemSelected, resetMenu) => (
        <>
          <EditItemPopover
            opened={menuItemSelected === 'Edit properties'}
            onClose={resetMenu}
            onSubmit={onSubmit}
            defaultValues={{
              ...item,
              unitOfMeasure: uoms?.find((u) => u.unitOfMeasureId === item.unitOfMeasureId),
              manufacturer: manufacturers?.find((m) => m.manufacturerId === item.manufacturerId),
            }}
          />
          {canAddToCatalog && (
            <AddToCatalogModal
              onClose={resetMenu}
              opened={menuItemSelected === 'Add item to catalog'}
              workOrderItem={item}
              onWorkOrderItemUpdated={refreshItem}
            />
          )}
          {canAddToShipment && (
            <AddToShipmentFromWorkOrderModal
              workOrder={workOrder}
              initialValues={{
                items: [
                  {
                    workOrderItemId: item.workOrderItemId,
                    workOrderId: item.workOrderId,
                    name: item.name,
                    quantity: Math.max(item.totalQuantity - item.totalAllocatedQuantity, 0),
                  },
                ],
                allSelectedWorkOrderIds: [],
              }}
              opened={menuItemSelected === 'Add to shipping order'}
              onClose={resetMenu}
              onItemsAdded={onMultipleItemsUpdated}
            />
          )}
        </>
      )}
    </GridMenu>
  );
};
